import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { WaveLine } from "../components/site/Wave";

const Shell = ({ title, sub, children }) => (
  <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-2 gap-10 items-center">
    <div className="hidden lg:block relative rounded-[28px] bg-[#0B1013] h-[460px] overflow-hidden p-10 text-white">
      <WaveLine className="absolute inset-x-0 top-1/3 h-28" color="#06B6D4" opacity={0.5} />
      <WaveLine className="absolute inset-x-0 top-1/2 h-28" color="#0D9488" slow opacity={0.5} />
      <img src="/logo-white.png" alt="CuraWaves" className="relative h-6 w-auto mb-8" />
      <div className="relative font-display text-3xl max-w-xs">Plans, orders and your health documents — in one place.</div>
      <p className="absolute bottom-10 left-10 right-10 text-sm text-slate-400">Your monthly re-assessment keeps your AUTO-code program tuned as you heal.</p>
    </div>
    <div className="bg-white rounded-[24px] border border-[#E4DFD4] p-8 sm:p-10">
      <h1 className="font-display text-3xl font-semibold">{title}</h1>
      <p className="text-sm text-slate-600 mt-2">{sub}</p>
      <div className="mt-8">{children}</div>
    </div>
  </div>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const next = params.get("next") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(form.email, form.password); nav(next); } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Welcome back" sub="Sign in to view your plans, orders and re-assessments.">
      <form onSubmit={submit} className="space-y-5" data-testid="login-form">
        <label className="block"><span className="field-label">Email</span><input data-testid="login-email-input" type="email" required className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="block"><span className="field-label">Password</span><input data-testid="login-password-input" type="password" required className="field" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
        <button data-testid="login-submit-btn" disabled={busy} className="w-full rounded-full bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-60 text-white py-3.5 font-semibold transition-colors">{busy ? "Signing in…" : "Sign in"}</button>
      </form>
      <Link to="/forgot-password" data-testid="login-forgot-link" className="block text-sm text-slate-600 hover:text-[#0D9488] mt-4">Forgot your password?</Link>
      <p className="text-sm text-slate-600 mt-4">New here? <Link data-testid="login-register-link" to={`/register?next=${encodeURIComponent(next)}`} className="text-[#0D9488] font-semibold">Create an account</Link></p>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const next = params.get("next") || "/questionnaire";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register(form.name, form.email, form.password); nav(next); } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your account" sub="Save your plans, upload lab reports and get monthly re-assessment reminders.">
      <form onSubmit={submit} className="space-y-5" data-testid="register-form">
        <label className="block"><span className="field-label">First name</span><input data-testid="register-name-input" required className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="block"><span className="field-label">Email</span><input data-testid="register-email-input" type="email" required className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="block"><span className="field-label">Password (min 6 characters)</span><input data-testid="register-password-input" type="password" minLength={6} required className="field" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></label>
        <button data-testid="register-submit-btn" disabled={busy} className="w-full rounded-full bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-60 text-white py-3.5 font-semibold transition-colors">{busy ? "Creating…" : "Create account"}</button>
      </form>
      <p className="text-sm text-slate-600 mt-6">Already have an account? <Link data-testid="register-login-link" to={`/login?next=${encodeURIComponent(next)}`} className="text-[#0D9488] font-semibold">Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Reset your password" sub="Enter your account email and we'll send you a secure reset link.">
      {sent ? (
        <p data-testid="forgot-sent-msg" className="text-sm text-slate-700">If an account exists for <b>{email}</b>, a reset link is on its way. It expires in 1 hour.</p>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <label className="block"><span className="field-label">Email</span><input data-testid="forgot-email-input" type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <button data-testid="forgot-submit-btn" disabled={busy} className="w-full rounded-full bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-60 text-white py-3.5 font-semibold transition-colors">{busy ? "Sending…" : "Send reset link"}</button>
        </form>
      )}
      <Link to="/login" className="block text-sm text-[#0D9488] font-semibold mt-6">Back to sign in</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token: params.get("token") || "", password });
      toast.success("Password updated — please sign in.");
      nav("/login");
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Choose a new password" sub="Your new password must be at least 6 characters.">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <label className="block"><span className="field-label">New password</span><input data-testid="reset-password-input" type="password" minLength={6} required className="field" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
        <button data-testid="reset-submit-btn" disabled={busy} className="w-full rounded-full bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-60 text-white py-3.5 font-semibold transition-colors">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </Shell>
  );
}
